import { zodResolver } from '@hookform/resolvers/zod'
import {
  ChevronRightIcon,
  Divider,
  EditOutlineIcon,
  ListItem,
  ListItemContent,
} from '@mr-yum/frontend-ui'
import {
  GuestReviewFormValues,
  GuestReviewSheet,
} from 'components/GuestReview/GuestReviewSheet'
import { ResponsiveModal } from 'components/ResponsiveModal'
import { useLogger } from 'hooks/useLogger'
import React, { useCallback, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { validationSchema } from './schema'
import { StarRatings } from './StarRatings'

export interface GuestReviewProps extends GuestReviewFormValues {
  venueName: string
  orderId: string
  reviewId: string | null
  disabled?: boolean
}

export const GuestReview = ({
  venueName,
  rating,
  notes,
  orderId,
  reviewId,
  disabled = false,
}: GuestReviewProps) => {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const { logEvent } = useLogger()
  const form = useForm<GuestReviewFormValues>({
    defaultValues: {
      notes,
      rating,
    },
    resolver: zodResolver(validationSchema),
  })

  const { setValue, clearErrors } = form
  const handleClose = useCallback(async () => {
    logEvent('Closed Review Notes Modal', {
      rating,
    })
    // reset rating/notes back to initial
    setValue('rating', rating)
    setValue('notes', notes)
    clearErrors()
    setOpen(false)
  }, [logEvent, rating, notes, setValue, clearErrors])

  const onDone = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  return (
    <FormProvider {...form}>
      <div className="space-y-2">
        <div className="space-y-4 px-4 pt-5">
          <div className="space-y-2">
            <h3 className="my-heading-sm">
              <FormattedMessage
                defaultMessage="How’s your experience going at {venueName}?"
                values={{ venueName }}
                id="RN1nHP"
              />
            </h3>
            <p className="my-body-md">
              <FormattedMessage
                defaultMessage="Your feedback goes directly to the team."
                id="+CYG13"
              />
            </p>
          </div>

          <Controller
            name="rating"
            render={({ field }) => (
              <StarRatings
                rating={field.value}
                onChange={(val) => {
                  logEvent('Selected Star Rating', {
                    rating,
                  })
                  field.onChange(val)
                  setOpen(true)
                }}
                disabled={disabled}
              />
            )}
          />
        </div>

        {rating > 0 && (
          <div>
            <ListItem size="lg" asChild isInteractive>
              <button
                disabled={disabled}
                onClick={() => {
                  setOpen(true)
                }}
              >
                <ListItemContent
                  label={
                    notes
                      ? notes
                      : intl.formatMessage({
                          defaultMessage: 'Add a note to your review',
                          id: 'PIGaUg',
                        })
                  }
                  labelWeight="normal"
                  labelTruncated
                  size="lg"
                  leftIcon={<EditOutlineIcon className="self-start" />}
                  rightIcon={<ChevronRightIcon />}
                />
              </button>
            </ListItem>

            <Divider />
          </div>
        )}
      </div>

      <ResponsiveModal onClose={handleClose}>
        {open && (
          <GuestReviewSheet
            reviewId={reviewId}
            orderId={orderId}
            onDone={onDone}
            onClose={handleClose}
          />
        )}
      </ResponsiveModal>
    </FormProvider>
  )
}
